import React, { useEffect, useState } from "react";
import { Table, Select, InputNumber, message, Popconfirm } from "antd";
import type { TableColumnsType } from "antd";
import { tasksApis } from "../../../api/NewTasks";
import { useLocation } from "react-router";
import {
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  AddAccess,
  EditAccess,
  CancelAccess,
} from "../../../components/accessControl/AccessControl";
import { useAccessControl } from "../../../utils/useAccessControl";
import { priorityTone, statusTone } from "../shared/statusColors";

const { Option } = Select;

const DEPENDENCY_TYPES = [
  { name: "FS", value: 1 },
  { name: "FF", value: 2 },
  { name: "SS", value: 3 },
  { name: "SF", value: 4 },
];

const TaskDependencies = ({
  taskId,
  loadOnRefresh,
  isFromWorkUI,
  projectId: projectIdProp,
  derivedProjectStatus: derivedProjectStatusProp,
}: {
  taskId: string;
  loadOnRefresh: string;
  isFromWorkUI?: boolean;
  projectId?: number | string;
  derivedProjectStatus?: string;
}) => {  
  const location = useLocation();
  const { id: projectIdFromState, derivedProjectStatus: derivedProjectStatusFromState } = location.state || {};
  // Prefer props (from parent/modal) so dropdown works when not navigated with router state
  const projectIdRaw = projectIdProp ?? projectIdFromState;
  const projectId = projectIdRaw != null ? Number(projectIdRaw) : undefined;
  const derivedProjectStatus = derivedProjectStatusProp ?? derivedProjectStatusFromState;
  const [data, setData] = useState<any[]>([]);
  const [taskDropdownData, setTaskDropdownData] = useState<any[]>([]);
  const [editingKey, setEditingKey] = useState<string>("");

  // Get access control permissions
  const { canAdd, canEdit, canCancel } = useAccessControl();

  const fetchPredecessorsListData = async () => {
    try {
      const apiResponse = await tasksApis.fetchTaskPredecessors({
        taskId,
        derivedProjectStatus,
      });
      const { data, status } = apiResponse;
      if (status) return data;
    } catch (error) {
      console.error("Error fetching predecessors:", error);
      return [];
    }
  };

  const fetchSuccessorsListData = async () => {
    try {
      const apiResponse = await tasksApis.fetchTaskSuccessors({
        taskId,
        derivedProjectStatus,
      });
      const { data, status } = apiResponse;
      if (status) return data;
    } catch (error) {
      console.error("Error fetching successors:", error);
      return [];
    }
  };

  const fetchTaskDropDownData = async () => {
    const validProjectId = projectId != null && !Number.isNaN(projectId) && Number(projectId) > 0;
    if (!validProjectId) {
      console.warn("TaskDependencies: projectId is required for task dropdown; skipping fetch.");
      return [];
    }
    try {
      const apiResponse = await tasksApis.fetchTaskDropDown({
        projectId: Number(projectId),
        ...(derivedProjectStatus ? { derivedProjectStatus } : {}),
      });
      const { data, status } = apiResponse;
      if (status) {
        setTaskDropdownData(
          data?.map((item: any) => ({
            name: item.taskName,
            value: item.taskId,
          }))
        );
      }
      return [];
    } catch (error) {
      console.error("Error fetching task dropdown:", error);
      return [];
    }
  };

  const loadDependencies = async () => {
    const [PredecessorsrespoData, SuccessorsrespoData] = await Promise.all([
      fetchPredecessorsListData(),
      fetchSuccessorsListData(),
    ]);

    const grouped = [
      { isGroupTitle: true, key: "title-predecessor", group: "Predecessor" },
      ...(PredecessorsrespoData || []).map((item: any) => ({
        key: `predecessor-${item.taskDependencyId}`,
        taskName: item.predecessorTaskName,
        id: item.predecessorTaskId,
        taskDependencyId: item.taskDependencyId,
        dependencyType: item.dependencyTypeId,
        dependencyTypeName: item.dependencyTypeName,
        lag: item.lagDays,
        group: "Predecessor",
        status: item.status,
        owner: item.predecessorOwnerName,
        startDate: item.predecessorStartDate,
        endDate: item.predecessorEndDate,
        duration: item.predecessorDuration,
        priority: item.predecessorPriorityName,
        workHours: item.predecessorWorkHoursInSec,
        isExisting: true,
      })),
      {
        key: `new-predecessor-${Date.now()}`,
        group: "Predecessor",
        isNew: true,
        taskName: null,
        dependencyType: null,
        lag: null,
        id: `new-predecessor-${Date.now()}`,
      },
      { isGroupTitle: true, key: "title-successor", group: "Successor" },
      ...(SuccessorsrespoData || []).map((item: any) => ({
        key: `successor-${item.taskDependencyId}`,
        taskName: item.successorTaskName,
        id: item.successorTaskId,
        taskDependencyId: item.taskDependencyId,
        dependencyType: item.dependencyTypeId,
        dependencyTypeName: item.dependencyTypeName,
        lag: item.lagDays,
        group: "Successor",
        status: item.status,
        owner: item.successorOwnerName,
        startDate: item.successorStartDate,
        endDate: item.successorEndDate,
        duration: item.successorDuration,
        priority: item.successorPriorityName,
        workHours: item.successorWorkHoursInSec,
        isExisting: true,
      })),
      {
        key: `new-successor-${Date.now()}`,
        group: "Successor",
        isNew: true,
        taskName: null,
        dependencyType: null,
        lag: null,
        id: `new-successor-${Date.now()}`,
      },
    ];

    setData(grouped);
  };

  useEffect(() => {
    if (loadOnRefresh === "dependencies") {
      fetchTaskDropDownData();
      loadDependencies();
    }
  }, [taskId, loadOnRefresh, projectId]);

  const isEditing = (record: any) => record.key === editingKey;

  const edit = (record: any) => {
    if(isFromWorkUI) return;
    setEditingKey(record.key);
  };

  const cancel = () => {
    if(isFromWorkUI) return;
    setEditingKey("");
  };

  const save = async (key: string) => {
    if(isFromWorkUI) return;
    try {
      const record = data.find((item) => key === item.key);

      if (record?.isNew) {
        await handleNewDependency(record);
      } else if (record?.isExisting) {
        await handleEditDependency(record);
      }

      setEditingKey("");
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  const handleNewDependency = async (record: any) => {
    if(isFromWorkUI) return;
    let dependencyData = {};
    if (record.group === "Predecessor") {
      dependencyData = {
        predecessorTaskId: record.taskName,
        successorTaskId: taskId,
        dependencyTypeId: record.dependencyType || 1,
        lagDays: record.lag || 0,
      };
    } else if (record.group === "Successor") {
      dependencyData = {
        predecessorTaskId: taskId,
        successorTaskId: record.taskName,
        dependencyTypeId: record.dependencyType || 1,
        lagDays: record.lag || 0,
      };
    }

    try {
      const apiResponse = await tasksApis.createTaskDependency([
        dependencyData,
      ]);
      const { message: apiMessage, status } = apiResponse;
      if (status) {
        message.success(apiMessage);
        await loadDependencies();
      } else {
        loadDependencies();
        message.error(apiMessage);
      }
    } catch (error) {
      loadDependencies();
      message.error("Failed to create task dependency");
      console.error("Error creating dependency:", error);
    }
  };

  const handleEditDependency = async (record: any) => {
    if(isFromWorkUI) return;
    if (!record.dependencyType || !record.taskDependencyId) return;

    try {
      const apiResponse = await tasksApis.updateTaskDependency({
        projectId: projectId,
        newLines: [],
        updatedLines: [
          {
            taskDependencyId: record.taskDependencyId,
            dependencyTypeId: record.dependencyType,
            lagDays: record.lag || 0,
          },
        ],
        deletedLines: [],
      });

      const { status, message: apiMessage } = apiResponse;
      if (status) {
        message.success("Dependency updated successfully");
        await loadDependencies();
      } else {
        message.error(apiMessage);
      }
    } catch (error) {
      message.error("Failed to update task dependency");
      console.error("Error updating dependency:", error);
    }
  };

  const handleDeleteDependency = async (record: any) => {
    if(isFromWorkUI) return;
    if (!record.dependencyType || !record.taskDependencyId) return;

    try {
      const apiResponse = await tasksApis.updateTaskDependency({
        projectId: projectId,
        newLines: [],
        updatedLines: [],
        deletedLines: [
          {
            taskDependencyId: record.taskDependencyId,
          },
        ],
      });

      const { status, message: apiMessage } = apiResponse;
      if (status) {
        message.success("Dependency deleted successfully");
        await loadDependencies();
      } else {
        message.error(apiMessage);
      }
    } catch (error) {
      message.error("Failed to delete task dependency");
      console.error("Error deleting dependency:", error);
    }
  };

  // Check if user has any action permissions (add, edit, or cancel)
  const hasActionPermissions =
    canAdd("Tasks") || canEdit("Tasks") || canCancel("Tasks");

  // Mirror the GridView popup filtering: when picking a task in the
  // Predecessor row, hide tasks already in Successors (and the current task);
  // when picking a task in the Successor row, hide tasks already in
  // Predecessors (and the current task). Prevents picking yourself or
  // creating an immediate circular dependency.
  const currentTaskIdStr = taskId != null ? String(taskId) : "";
  const existingPredecessorIds = data
    .filter((d: any) => d.group === "Predecessor" && d.isExisting && d.id != null)
    .map((d: any) => String(d.id));
  const existingSuccessorIds = data
    .filter((d: any) => d.group === "Successor" && d.isExisting && d.id != null)
    .map((d: any) => String(d.id));

  const getTaskOptionsForRecord = (record: any) => {
    if (!record) return taskDropdownData;
    if (record.group === "Predecessor") {
      return taskDropdownData.filter(
        (item: any) =>
          String(item.value) !== currentTaskIdStr &&
          !existingSuccessorIds.includes(String(item.value)),
      );
    }
    if (record.group === "Successor") {
      return taskDropdownData.filter(
        (item: any) =>
          String(item.value) !== currentTaskIdStr &&
          !existingPredecessorIds.includes(String(item.value)),
      );
    }
    return taskDropdownData;
  };

  const columns: any = [
    {
      title: "Task Name",
      dataIndex: "taskName",
      width: 200,
      render: (value: any, record: any) => {
        if (record.isGroupTitle) {
          return {
            children: (
              <div style={{ fontWeight: "500", color: "gray", fontSize: 16 }}>
                {record.group === "Predecessor"
                  ? "Predecessor Tasks"
                  : "Successor Tasks"}
              </div>
            ),
            props: { colSpan: hasActionPermissions ? 11 : 10 }, // Adjust colspan based on action column visibility
          };
        }

        if (record.isNew) {
          return (
            <AddAccess moduleName="Tasks">
              <span
                style={{
                  color: "#2557d6",
                  fontStyle: "italic",
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
                onClick={() => edit(record)}
              >
                Add {record.group}
              </span>
            </AddAccess>
          );
        }

        const selectedItem = taskDropdownData.find(
          (item) => item.value === value
        );
        return selectedItem ? selectedItem.name : value;
      },
      editable: true,
    },
    {
      title: "Dependency Type",
      dataIndex: "dependencyType",
      width: 150,
      render: (value: any, record: any) => {
        if (record.isGroupTitle)
          return { children: null, props: { colSpan: 0 } };
        const selectedItem = DEPENDENCY_TYPES.find(
          (item) => item.value === value
        );
        return selectedItem ? selectedItem.name : value;
      },
      editable: true,
    },
    {
      title: "Lag",
      dataIndex: "lag",
      width: 100,
      editable: true,
      render: (value: any, record: any) => {
        if (record.isGroupTitle)
          return { children: null, props: { colSpan: 0 } };
        return value;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 120,
      render: (value: any, record: any) => {
        if (record.isGroupTitle)
          return { children: null, props: { colSpan: 0 } };
        const tone = statusTone(value);
        return (
          <span
            style={{
              backgroundColor: tone.bg,
              color: tone.text,
              padding: "2px 8px",
              borderRadius: "4px",
            }}
          >
            {value}
          </span>
        );
      },
    },
    {
      title: "Owner",
      dataIndex: "owner",
      width: 120,
      render: (value: any, record: any) => {
        if (record.isGroupTitle)
          return { children: null, props: { colSpan: 0 } };
        return value;
      },
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      width: 120,
      render: (value: any, record: any) => {
        if (record.isGroupTitle)
          return { children: null, props: { colSpan: 0 } };
        return record.isNew
          ? ""
          : value
          ? dayjs(value).format("DD-MMM-YYYY")
          : "N/A";
      },
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      width: 120,
      render: (value: any, record: any) => {
        if (record.isGroupTitle)
          return { children: null, props: { colSpan: 0 } };
        return record.isNew
          ? ""
          : value
          ? dayjs(value).format("DD-MMM-YYYY")
          : "N/A";
      },
    },
    {
      title: "Duration",
      dataIndex: "duration",
      width: 100,
      render: (value: any, record: any) => {
        if (record.isGroupTitle)
          return { children: null, props: { colSpan: 0 } };
        return value;
      },
    },
    {
      title: "Priority",
      dataIndex: "priority",
      width: 100,
      render: (value: any, record: any) => {
        if (record.isGroupTitle)
          return { children: null, props: { colSpan: 0 } };
        const color = priorityTone(value).text;
        return <span style={{ color, fontWeight: "bold" }}>{value}</span>;
      },
    },
    {
      title: "Work Hours",
      dataIndex: "workHours",
      width: 100,
      render: (value: any, record: any) => {
        if (record.isGroupTitle)
          return { children: null, props: { colSpan: 0 } };
        return value;
      },
    },
    // Conditionally include action column only if user has action permissions
    ...(hasActionPermissions
      ? [
          {
            title: "Action",
            dataIndex: "",
            width: 100,
            fixed: "right",
            render: (_: any, record: any) => {
              if (record.isGroupTitle)
                return { children: null, props: { colSpan: 0 } };

              const editable = isEditing(record);

              // For new records: show Save/Cancel while editing, otherwise
              // the Add trigger. Without the editable branch the Save button
              // never appears after "Add Predecessor"/"Add Successor".
              if (record.isNew) {
                return editable ? (
                  <>
                    <AddAccess moduleName="Tasks">
                      <a
                        onClick={() => save(record.key)}
                        style={{ marginRight: 8 }}
                      >
                        <SaveOutlined />
                      </a>
                    </AddAccess>
                    <a onClick={cancel}>
                      <CloseOutlined />
                    </a>
                  </>
                ) : (
                  <AddAccess moduleName="Tasks">
                    <a
                      onClick={() => edit(record)}
                      style={{ color: "#2557d6" }}
                    >
                      <EditOutlined />
                    </a>
                  </AddAccess>
                );
              }

              // For existing records, show edit/save/delete buttons
              return editable ? (
                <>
                  <EditAccess moduleName="Tasks">
                    <a
                      onClick={() => save(record.key)}
                      style={{ marginRight: 8 }}
                    >
                      <SaveOutlined />
                    </a>
                  </EditAccess>
                  {/* <CancelAccess moduleName="Tasks"> */}
                    <a onClick={cancel}>
                      <CloseOutlined />
                    </a>
                  {/* </CancelAccess> */}
                </>
              ) : (
                <>
                  <AddAccess moduleName="Tasks">
                    <a onClick={() => edit(record)}>
                      <EditOutlined />
                    </a>
                  </AddAccess>
                  {record.isExisting && (
                    <CancelAccess moduleName="Tasks">
                      <Popconfirm
                        title="Are you sure to delete this dependency?"
                        onConfirm={() => handleDeleteDependency(record)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <a>
                          <DeleteOutlined
                            style={{ color: "red", marginLeft: 8 }}
                          />
                        </a>
                      </Popconfirm>
                    </CancelAccess>
                  )}
                </>
              );
            },
          },
        ]
      : []),
  ];

  const mergedColumns = columns.map((col: any) => {
    if (!col.editable || isFromWorkUI) {
      return col;
    }

    return {
      ...col,
      onCell: (record: any) => {
        let editable = isEditing(record);

        if (col.dataIndex === "taskName") {
          editable = record.isNew && isEditing(record);
        }

        return {
          record,
          inputType:
            col.dataIndex === "taskName" || col.dataIndex === "dependencyType"
              ? "select"
              : col.dataIndex === "lag"
              ? "number"
              : "text",
          dataIndex: col.dataIndex,
          title: col.title,
          editing: editable,
          options:
            col.dataIndex === "taskName"
              ? getTaskOptionsForRecord(record)
              : col.dataIndex === "dependencyType"
              ? DEPENDENCY_TYPES
              : [],
        };
      },
    };
  });

  const EditableCell = ({
    editing,
    dataIndex,
    title,
    inputType,
    record,
    index,
    children,
    options,
    ...restProps
  }: any) => {
    let inputNode;

    if (inputType === "select") {
      inputNode = (
        <Select
          style={{ width: "100%" }}
          placeholder={`Select ${title}`}
          showSearch
          optionFilterProp="children"
          value={record[dataIndex]}
          onChange={(value) => {
            const newData = [...data];
            const index = newData.findIndex((item) => record.key === item.key);
            if (index > -1) {
              newData[index][dataIndex] = value;
              setData(newData);
            }
          }}
        >
          {options.map((option: any) => (
            <Option key={option.value} value={option.value}>
              {option.name}
            </Option>
          ))}
        </Select>
      );
    } else if (inputType === "number") {
      inputNode = (
        <InputNumber
          style={{ width: "100%" }}
          value={record[dataIndex]}
          onChange={(value) => {
            const newData = [...data];
            const index = newData.findIndex((item) => record.key === item.key);
            if (index > -1) {
              newData[index][dataIndex] = value;
              setData(newData);
            }
          }}
        />
      );
    }

    return <td {...restProps}>{editing ? inputNode : children}</td>;
  };

  return (
    <Table
      components={{
        body: {
          cell: isFromWorkUI ? undefined : EditableCell,
        },
      }}
      bordered
      dataSource={data}
      columns={mergedColumns}
      rowClassName="editable-row"
      pagination={false}
      scroll={{
        x: hasActionPermissions ? 1350 : 1250, // Adjust scroll width based on action column visibility
      }}
    />
  );
};

export default TaskDependencies;
